import { BN } from '@coral-xyz/anchor';
import { getPdaLaunchpadPoolId, getPdaLaunchpadVaultId, getPdaPlatformVault, LAUNCHPAD_PROGRAM } from '@raydium-io/raydium-sdk-v2';
import {
    createAssociatedTokenAccountIdempotentInstruction,
    createAssociatedTokenAccountInstruction,
    createCloseAccountInstruction,
    createSyncNativeInstruction,
    getAssociatedTokenAddressSync,
    NATIVE_MINT,
    syncNative,
    TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import {
    ComputeBudgetProgram,
    Connection,
    Keypair,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { getZeroslotTipInstruction, sendZeroslotTransaction } from '../0slot/0slot';
import { toast } from 'react-toastify';
import { SYSTEM_PROGRAM } from '../utils/constants';

export async function buyLetsbonkToken(
    connection: Connection,
    mint: PublicKey,
    buyer: Keypair,
    creator: PublicKey,
    buyAmount: BN,
    zeroSlotTip: number,
    priorityFee: number = 1_000_000,
    priorityLimit: number = 200_000,

    //slippage: number = 10,
) {
    const discriminator = Buffer.from([0xfa, 0xea, 0x0d, 0x7b, 0xd5, 0x9c, 0x13, 0xec]);
    const amountInBuffer = Buffer.from(buyAmount.toArray('le', 8));
    const minAmountOutBuffer = Buffer.from(new BN(0).toArray('le', 8));
    const shareFeeRateBuffer = Buffer.from(new BN(0).toArray('le', 8));

    const instructionData = Buffer.concat([
        Uint8Array.from(discriminator),
        Uint8Array.from(amountInBuffer),
        Uint8Array.from(minAmountOutBuffer),
        Uint8Array.from(shareFeeRateBuffer),
    ]);

    const buyInstruction = new TransactionInstruction({
        keys: [
            { pubkey: buyer.publicKey, isSigner: true, isWritable: true },
            { pubkey: new PublicKey('WLHv2UAZm6z4KyaaELi5pjdbJh6RESMva1Rnn8pJVVh'), isSigner: false, isWritable: false },
            { pubkey: new PublicKey('6s1xP3hpbAfFoNtUNF8mfHsjr2Bd97JxFJRWLbL6aHuX'), isSigner: false, isWritable: false },
            { pubkey: new PublicKey('FfYek5vEz23cMkWsdJwG2oa6EphsvXSHrGpdALN4g6W1'), isSigner: false, isWritable: false },
            { pubkey: getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, mint, NATIVE_MINT).publicKey, isSigner: false, isWritable: true },
            { pubkey: getAssociatedTokenAddressSync(mint, buyer.publicKey), isSigner: false, isWritable: true },
            { pubkey: getAssociatedTokenAddressSync(NATIVE_MINT, buyer.publicKey), isSigner: false, isWritable: true },
            {
                pubkey: getPdaLaunchpadVaultId(LAUNCHPAD_PROGRAM, getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, mint, NATIVE_MINT).publicKey, mint).publicKey,
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getPdaLaunchpadVaultId(LAUNCHPAD_PROGRAM, getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, mint, NATIVE_MINT).publicKey, NATIVE_MINT).publicKey,
                isSigner: false,
                isWritable: true,
            },
            { pubkey: mint, isSigner: false, isWritable: false },
            { pubkey: NATIVE_MINT, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: new PublicKey('2DPAtwB8L12vrMRExbLuyGnC7n2J5LNoZQSejeQGpwkr'), isSigner: false, isWritable: false },
            { pubkey: new PublicKey('LanMV9sAd7wArD4vJFi2qDdfnVhFxYSUg6eADduJ3uj'), isSigner: false, isWritable: false },
            { pubkey: SYSTEM_PROGRAM, isSigner: false, isWritable: false },
            {
                pubkey: getPdaPlatformVault(LAUNCHPAD_PROGRAM, new PublicKey('FfYek5vEz23cMkWsdJwG2oa6EphsvXSHrGpdALN4g6W1'), NATIVE_MINT).publicKey,
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: PublicKey.findProgramAddressSync([creator.toBuffer(), NATIVE_MINT.toBuffer()], LAUNCHPAD_PROGRAM)[0],
                isSigner: false,
                isWritable: true,
            },
        ],
        programId: new PublicKey('LanMV9sAd7wArD4vJFi2qDdfnVhFxYSUg6eADduJ3uj'),
        data: instructionData,
    });

    const tx = new VersionedTransaction(
        new TransactionMessage({
            payerKey: buyer.publicKey,
            recentBlockhash: (await connection.getLatestBlockhash('confirmed')).blockhash,
            instructions: [
                ComputeBudgetProgram.setComputeUnitLimit({ units: priorityLimit }),
                ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }),
                createAssociatedTokenAccountIdempotentInstruction(
                    buyer.publicKey,
                    getAssociatedTokenAddressSync(NATIVE_MINT, buyer.publicKey),
                    buyer.publicKey,
                    new PublicKey(NATIVE_MINT),
                ),
                SystemProgram.transfer({
                    fromPubkey: buyer.publicKey,
                    toPubkey: getAssociatedTokenAddressSync(NATIVE_MINT, buyer.publicKey),
                    lamports: buyAmount,
                }),
                createSyncNativeInstruction(getAssociatedTokenAddressSync(NATIVE_MINT, buyer.publicKey)),

                createAssociatedTokenAccountInstruction(buyer.publicKey, getAssociatedTokenAddressSync(mint, buyer.publicKey), buyer.publicKey, mint),
                buyInstruction,
                createCloseAccountInstruction(getAssociatedTokenAddressSync(NATIVE_MINT, buyer.publicKey), buyer.publicKey, buyer.publicKey),
                getZeroslotTipInstruction(buyer.publicKey, zeroSlotTip),
            ],
        }).compileToV0Message(),
    );

    tx.sign([buyer]);

    const hash = await sendZeroslotTransaction(tx);

    toast.success(`LETSBONK buy transaction sent: ${hash}`);

    return hash;
}
